import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import useGameStats from './useGameStats';
import useAchievements from './useAchievements';
import useDailyGameState from './useDailyGameState';
import { useAuth } from '../contexts/AuthContext';
import { prioritizedSearch } from '../utils/searchUtils';
import { smoothScrollToElement } from '../utils/smoothScroll';
import { getCentralDateString } from '../utils/centralTime';
import apiService from '../services/api';

// Use centralized date function directly

/**
 * Custom hook that extracts all GameEngine logic while preserving exact variable names
 * This ensures the JSX remains unchanged and CSS classes are generated correctly
 */
const useGameLogic = (config) => {
  const {
    // Game identity
    gameName,
    gameId,
    
    // API endpoints
    apiEndpoint,
    dailyApiEndpoint,
    
    // Game mechanics
    categories,
    compareFunction,
    getDisplayValue,
    
    // Components
    HintComponent,
    DirectoryComponent,
    HowToPlayComponent,
    
    // CSS configuration
    cssPrefix,
    
    // Item naming
    itemName,
    itemNamePlural,
    itemNameCapitalized,
    itemNameSingular,
    
    // Storage keys
    sessionStorageKey,
    dailyGameKey,
    streakKey,
    
    // UI text
    placeholderText,
    directoryButtonText,
    
    // Custom renderers
    renderVictoryDetails,
    
    // Data field mapping
    dailyDataField = itemName,
    itemsArrayField = null
  } = config;

  // State management - EXACT same names as original GameEngine
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const [guessedItems, setGuessedItems] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [attempts, setAttempts] = useState([]);
  const [gameWon, setGameWon] = useState(false);
  const [justWon, setJustWon] = useState(false);
  const [showIndicators, setShowIndicators] = useState(true);
  const [isHintRevealed, setIsHintRevealed] = useState(false);
  const [showDirectory, setShowDirectory] = useState(false);
  const [gameKey, setGameKey] = useState(Date.now());
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [isDailyMode, setIsDailyMode] = useState(true);
  const [dailyCompleted, setDailyCompleted] = useState(false);
  const [todayDate, setTodayDate] = useState(getCentralDateString());
  const [currentStreak, setCurrentStreak] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [triggerConfetti, setTriggerConfetti] = useState(false);

  // Authentication
  const { loading: authLoading, user, isAuthenticated } = useAuth();

  // Game statistics tracking
  const { trackGameCompletion, recordLocalDailyWin, getLocalStreak } = useGameStats(gameId);
  const { processGameCompletionResponse } = useAchievements();
  
  // Daily game state management
  const { loadDailyGameState, saveDailyGameState } = useDailyGameState(gameId);

  // Refs - EXACT same names as original GameEngine
  const guessInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const suggestionItemsRef = useRef([]);
  const skipNextFocus = useRef(false);

  // Tooltip content
  const dailyChallengeTooltip = (
    <div>
      <h4>Daily Challenge</h4>
      <div style={{ marginTop: '0.5rem' }}>
        • Same {itemName} for all players each day<br/>
        • Complete to maintain your streak<br/>
        • New challenge resets at midnight<br/>
        • Miss a day = streak resets to 0
      </div>
    </div>
  );

  const endlessModeTooltip = (
    <div>
      <strong>Endless Mode</strong>
      <div style={{ marginTop: '0.5rem' }}>
        • Random {itemNamePlural} for unlimited practice<br/>
        • Play as many times as you want<br/>
        • Doesn't affect your daily streak<br/>
      </div>
    </div>
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        guessInputRef.current && 
        !guessInputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Scroll to highlighted item when it changes
  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionItemsRef.current[highlightedIndex] && dropdownRef.current) {
      const item = suggestionItemsRef.current[highlightedIndex];
      const dropdown = dropdownRef.current;
      
      const itemTop = item.offsetTop;
      const itemBottom = itemTop + item.offsetHeight;
      const dropdownScrollTop = dropdown.scrollTop;
      const dropdownHeight = dropdown.clientHeight;
      
      if (itemBottom > dropdownScrollTop + dropdownHeight) {
        dropdown.scrollTop = itemBottom - dropdownHeight;
      } else if (itemTop < dropdownScrollTop) {
        dropdown.scrollTop = itemTop;
      }
    }
  }, [highlightedIndex]);

  // Load streak
  const loadStreak = useCallback(async () => {
    if (isAuthenticated && user) {
      try {
        const response = await apiService.get(`/api/daily-game-states/user/${user.id}/summary/${todayDate}`);
        const data = await response.json();
        
        const gameData = data.gameData?.[gameId];
        if (gameData) {
          setCurrentStreak(gameData.currentStreak || 0);
          return gameData.currentStreak || 0;
        }
      } catch (error) {
        console.error('Error loading streak from summary:', error);
      }
    } else {
      const localStreak = getLocalStreak(streakKey);
      setCurrentStreak(localStreak);
      return localStreak;
    }
    setCurrentStreak(0);
    return 0;
  }, [isAuthenticated, user, getLocalStreak, todayDate, gameId, streakKey]);

  const loadDailyGameStateLocal = useCallback(() => {
    return loadDailyGameState(dailyGameKey);
  }, [loadDailyGameState, dailyGameKey]);

  const saveDailyGameStateLocal = useCallback((state) => {
    return saveDailyGameState(state, dailyGameKey);
  }, [saveDailyGameState, dailyGameKey]);

  const updateStreak = useCallback(async (gameState) => {
    if (isAuthenticated) {
      if (gameState) {
        await saveDailyGameStateLocal(gameState);
      }
      
      // Pass attempts count for immediate Flawless Victory detection
      const attemptsCount = gameState?.attempts?.length;
      const updatedStats = await trackGameCompletion(true, attemptsCount);
      if (updatedStats) {
        await processGameCompletionResponse(updatedStats);
        await loadStreak();
      }
    } else {
      // Save the full game state to localStorage for the daily page to read
      if (gameState) {
        await saveDailyGameStateLocal(gameState);
      }

      // Track daily completion on server for anonymous users
      const attemptsCount = gameState?.attempts?.length;
      await trackGameCompletion(true, attemptsCount);

      // Then, update the streak as before
      const newStreak = recordLocalDailyWin(streakKey);
      setCurrentStreak(newStreak);
    }
  }, [isAuthenticated, trackGameCompletion, recordLocalDailyWin, processGameCompletionResponse, saveDailyGameStateLocal, loadStreak, streakKey]);

  // Search suggestions
  const suggestions = useMemo(() => {
    return prioritizedSearch(items, currentGuess, 'name', guessedItems, 10);
  }, [currentGuess, items, guessedItems]);

  const showSuggestions = useMemo(() => {
    return currentGuess.trim().length > 0 && suggestions.length > 0 && showDropdown;
  }, [currentGuess, suggestions, showDropdown]);

  const isValidGuess = useMemo(() => {
    if (!currentGuess.trim()) return false;
    // Allow capybara easter egg
    if (currentGuess.toLowerCase() === 'capybara') return true;
    return items.some(item => item.name.toLowerCase() === currentGuess.toLowerCase());
  }, [currentGuess, items]);

  // Fetch daily item
  const fetchDailyItem = useCallback(async () => {
    try {
      const res = await apiService.get(dailyApiEndpoint);
      const data = await res.json();
      const dailyItem = data[dailyDataField];
      setCurrentItem(dailyItem);
      setTodayDate(data.date);
      setIsDailyMode(true);
      setDailyCompleted(false);
      sessionStorage.removeItem(sessionStorageKey);
      
      if (user?.role === 'owner' && dailyItem) {
        console.log(`🎯 [Owner Debug] ${gameId}`, dailyItem.name);
      }
    } catch (err) {
      console.error(`Error fetching daily ${itemName}:`, err);
    }
  }, [user, dailyApiEndpoint, itemName, sessionStorageKey, gameId, dailyDataField]);

  // Fetch items data
  const fetchItemsData = useCallback(async () => {
    try {
      const response = await apiService.get(apiEndpoint);
      const data = await response.json();
      setItems(data);
      
      const savedState = await loadDailyGameStateLocal();
      if (savedState) {
        setCurrentItem(savedState.currentCard);
        setAttempts(savedState.attempts || []);
        setGuessedItems(savedState.guessedCards || []);
        setGameWon(savedState.gameWon || false);
        setIsHintRevealed(savedState.isHintRevealed || false);
        setDailyCompleted(savedState.gameWon || false);
        setIsDailyMode(true);
        sessionStorage.removeItem(sessionStorageKey);
      } else {
        fetchDailyItem();
      }
    } catch (error) {
      console.error(`Error fetching ${itemNamePlural}:`, error);
    }
  }, [loadDailyGameStateLocal, fetchDailyItem, apiEndpoint, sessionStorageKey, itemNamePlural]);

  useEffect(() => {
    if (!authLoading) {
      loadStreak();
      fetchItemsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  // Auto-scroll on victory (both just won and returning to completed)
  useEffect(() => {
    if (gameWon) {
      setTimeout(() => {
        const victoryElement = document.querySelector(`.${cssPrefix}__victory-message`);
        if (victoryElement) {
          smoothScrollToElement(victoryElement, 600, 'end');
        }
      }, 100);
    }
  }, [gameWon, cssPrefix]);

  // Confetti only on fresh victory
  useEffect(() => {
    if (justWon) {
      setTimeout(() => {
        setTriggerConfetti(true);
      }, 800);
    }
  }, [justWon]);

  // Select random item
  const selectRandomItem = useCallback((itemList) => {
    if (!itemList || itemList.length === 0) return;
    const randomIndex = Math.floor(Math.random() * itemList.length);
    const selectedItem = itemList[randomIndex];
    setCurrentItem(selectedItem);
    
    if (user?.role === 'owner' && selectedItem) {
      console.log(`🎯 [Owner Debug] ${gameId} - Correct answer:`, selectedItem.name);
    }
  }, [user, gameId]);

  // Input handlers
  const handleInputChange = useCallback((e) => {
    setCurrentGuess(e.target.value);
    if (e.target.value.trim().length > 0) {
      setShowDropdown(true);
      setHighlightedIndex(-1);
    } else {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  }, []);

  const handleInputFocus = useCallback(() => {
    if (skipNextFocus.current) {
      skipNextFocus.current = false;
      return;
    }
    if (currentGuess.trim().length > 0 && suggestions.length > 0) {
      setShowDropdown(true);
    }
    setHighlightedIndex(-1);
  }, [currentGuess, suggestions]);

  const handleSuggestionClick = useCallback((itemName) => {
    setCurrentGuess(itemName);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    guessInputRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          e.preventDefault();
          handleSuggestionClick(suggestions[highlightedIndex].name);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  }, [showDropdown, suggestions, highlightedIndex, handleSuggestionClick]);

  // Submit guess
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Check for capybara easter egg FIRST (before other validations)
    if (currentGuess.trim().toLowerCase() === 'capybara' && isAuthenticated) {
      try {
        const response = await apiService.submitEasterEgg('capybara');
        const data = await response.json();
        
        if (data.success && data.newAchievements?.length > 0) {
          // Process the achievement through the existing system
          await processGameCompletionResponse({ newAchievements: data.newAchievements });
        }
      } catch (error) {
        // Silent fail - don't interfere with normal gameplay
        console.debug('Easter egg check failed:', error);
      }
      
      // Clear the guess and return early - don't treat as a real guess
      setCurrentGuess('');
      return;
    }

    // Normal validation after easter egg check
    if (!currentGuess.trim() || !currentItem) return;

    const guessedItemObject = items.find(item => item.name.toLowerCase() === currentGuess.toLowerCase());
    if (!guessedItemObject) return;
    
    if (guessedItems.some(guessed => guessed.toLowerCase() === guessedItemObject.name.toLowerCase())) {
      setCurrentGuess('');
      return;
    }

    const attempt = {
      [itemName]: guessedItemObject,
      results: compareFunction(guessedItemObject, currentItem)
    };

    const newAttempts = [...attempts, attempt];
    const newGuessedItems = [...guessedItems, guessedItemObject.name];
    const isWon = guessedItemObject.name.toLowerCase() === currentItem.name.toLowerCase();

    setAttempts(newAttempts);
    setGuessedItems(newGuessedItems);
    setCurrentGuess('');
    setShowDropdown(false);
    setHighlightedIndex(-1);

    if (isWon) {
      setGameWon(true);
      setJustWon(true);
      if (isDailyMode) {
        setDailyCompleted(true);
        await updateStreak({
          currentCard: currentItem,
          attempts: newAttempts,
          guessedCards: newGuessedItems,
          gameWon: isWon,
          isHintRevealed
        });
      } else if (isAuthenticated) {
        // Pass attempts count for immediate Flawless Victory detection in endless mode
        const updatedStats = await trackGameCompletion(false, newAttempts.length);
        await processGameCompletionResponse(updatedStats);
      } else {
        // Track anonymous endless mode completions
        await trackGameCompletion(false, newAttempts.length);
      }
    } else if (isDailyMode) {
      await saveDailyGameStateLocal({
        currentCard: currentItem,
        attempts: newAttempts,
        guessedCards: newGuessedItems,
        gameWon: isWon,
        isHintRevealed
      });
    }
  }, [currentGuess, currentItem, items, guessedItems, attempts, compareFunction, isDailyMode, updateStreak, isAuthenticated, trackGameCompletion, saveDailyGameStateLocal, isHintRevealed, itemName, processGameCompletionResponse]);

  // Reset game
  const resetGame = useCallback(() => {
    setAttempts([]);
    setGuessedItems([]);
    setGameWon(false);
    setJustWon(false);
    setCurrentGuess('');
    setIsHintRevealed(false);
    setShowDirectory(false);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    sessionStorage.removeItem(sessionStorageKey);
    setGameKey(Date.now());
    setIsDailyMode(false);
    setDailyCompleted(false);
    selectRandomItem(items);
  }, [items, selectRandomItem, sessionStorageKey]);

  // Directory handlers
  const handleDirectorySelect = useCallback((itemName) => {
    skipNextFocus.current = true;
    setCurrentGuess(itemName);
    setShowDirectory(false);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    guessInputRef.current?.focus();
  }, []);

  const handleCloseIndicators = useCallback(() => setShowIndicators(false), []);
  
  const handleRevealHint = useCallback(async () => {
    setIsHintRevealed(true);
    
    if (isDailyMode) {
      await saveDailyGameStateLocal({
        currentCard: currentItem,
        attempts,
        guessedCards: guessedItems,
        gameWon,
        isHintRevealed: true
      });
    }
  }, [isDailyMode, currentItem, attempts, guessedItems, gameWon, saveDailyGameStateLocal]);

  const handleShowHowToPlay = useCallback(() => setShowHowToPlay(true), []);
  const handleCloseHowToPlay = useCallback(() => setShowHowToPlay(false), []);
  const handleShowDirectory = useCallback(() => setShowDirectory(true), []);
  const handleCloseDirectory = useCallback(() => setShowDirectory(false), []);

  // Directory component props mapping
  const directoryProps = {
    show: showDirectory,
    onClose: handleCloseDirectory,
    [itemsArrayField || `all${itemNameCapitalized}`]: items,
    [`guessed${itemNameCapitalized}`]: guessedItems,
    [`on${itemNameSingular || itemNameCapitalized.slice(0, -1)}Select`]: handleDirectorySelect,
    sessionStorageKey
  };

  // Hint component props mapping
  const hintProps = {
    attemptsCount: attempts.length,
    [`current${itemNameSingular || itemNameCapitalized.slice(0, -1)}`]: currentItem,
    gameWon,
    isHintRevealed,
    onRevealHint: handleRevealHint
  };

  // Return EVERYTHING with exact same names as original GameEngine
  return {
    // State variables (all 19)
    items,
    currentItem,
    guessedItems,
    currentGuess,
    attempts,
    gameWon,
    justWon,
    showIndicators,
    isHintRevealed,
    showDirectory,
    gameKey,
    showHowToPlay,
    isDailyMode,
    dailyCompleted,
    todayDate,
    currentStreak,
    showDropdown,
    highlightedIndex,
    triggerConfetti,
    
    // State setters (all 19)
    setItems,
    setCurrentItem,
    setGuessedItems,
    setCurrentGuess,
    setAttempts,
    setGameWon,
    setJustWon,
    setShowIndicators,
    setIsHintRevealed,
    setShowDirectory,
    setGameKey,
    setShowHowToPlay,
    setIsDailyMode,
    setDailyCompleted,
    setTodayDate,
    setCurrentStreak,
    setShowDropdown,
    setHighlightedIndex,
    setTriggerConfetti,
    
    // Refs (all 4)
    guessInputRef,
    dropdownRef,
    suggestionItemsRef,
    skipNextFocus,
    
    // Auth
    authLoading,
    user,
    isAuthenticated,
    
    // Callbacks
    handleInputChange,
    handleInputFocus,
    handleSuggestionClick,
    handleKeyDown,
    handleSubmit,
    resetGame,
    handleDirectorySelect,
    handleCloseIndicators,
    handleRevealHint,
    handleShowHowToPlay,
    handleCloseHowToPlay,
    handleShowDirectory,
    handleCloseDirectory,
    
    // Memoized values
    suggestions,
    showSuggestions,
    isValidGuess,
    
    // Props objects for child components
    directoryProps,
    hintProps,
    
    // Tooltip content
    dailyChallengeTooltip,
    endlessModeTooltip,
    
    // Config values needed in JSX
    cssPrefix,
    gameName,
    gameId,
    placeholderText,
    directoryButtonText,
    categories,
    itemName,
    getDisplayValue,
    renderVictoryDetails,
    HowToPlayComponent,
    DirectoryComponent,
    HintComponent
  };
};

export default useGameLogic;